import { useState, KeyboardEvent } from "react";
import {
    AuthSignInSocialButton,
    AuthSignInSubPage,
    AuthContentTitleWrapper,
    AuthContentTitle,
    PhoneInputWrapper,
    AuthSignInInputSubtitle,
    AuthSignInSocialIcon,
    AuthSignInSocialTitle,
} from "../authBarStyling";
import { ColorTheme } from "../../globaStyledComponents/globalStyledComponentsStyling";
import { useToast } from "../../toast/ToastContext";
import styled from "styled-components";
import { Button } from "../../ui/button";

import PhoneInput from "react-phone-number-input";
import ContinueBtn from "../../btn/continueBtn/continueBtn";
import { callApi } from "../../../utils/api/api";
import { Auth } from "aws-amplify";
import { CognitoHostedUIIdentityProvider } from "@aws-amplify/auth/lib/types";
import { useCookies } from "react-cookie";
import { useRecoilState } from "recoil";
import { googleLoginErrStateSelector } from "../../../state/selectors/googleLoginErr";

const LogoImage = styled.img`
    position: absolute;
    top: 24px;
    left: 50%;
    transform: translateX(-50%);
    height: 32px;
    width: auto;
    z-index: 103;

    @media (min-width: 768px) {
        top: 24px;
    }
`;

const CloseButton = styled(Button)`
    position: absolute;
    top: 16px;
    left: 16px;
    width: 44px;
    height: 44px;
    padding: 10px;
    border-radius: 50%;
    box-shadow: none;
    background: rgba(255, 255, 255, 0.85);
    border: 1px solid ${ColorTheme.White.color};
    box-shadow: 0 2px 16px rgba(0, 0, 0, 0.08);
    z-index: 104;
    
    &:hover {
        background: rgba(255, 255, 255, 0.85);
        box-shadow: 0 2px 16px rgba(0, 0, 0, 0.08);
        border: 1px solid ${ColorTheme.White.color};
    }
    &:active {
        background: rgba(255, 255, 255, 0.85);
        box-shadow: 0 2px 16px rgba(0, 0, 0, 0.08);
        border: 1px solid ${ColorTheme.White.color};
    }

    @media (min-width: 768px) {
        top: 24px;
        left: 24px;
    }
`;

const StyledPhoneInput = styled(PhoneInput)`
    & input {
        -webkit-appearance: none;
        -webkit-text-fill-color: black !important;
        transition: background-color 5000s ease-in-out 0s;

        &::placeholder {
            -webkit-text-fill-color: #999 !important;
            color: #999;
        }
    }
`;

interface PhonePopupProps {
    setPageName: (pageName: string) => void;
    setUsername: (userName: string) => void;
    setPhoneNumFromPhonePopup: (phoneNumFromPhonePopup: string) => void;
    handleKeyboardDismiss: () => void;
    onClose?: () => void;
}

const PhonePopup: React.FC<PhonePopupProps> = ({ setUsername, setPageName, setPhoneNumFromPhonePopup, handleKeyboardDismiss, onClose }) => {
    const [phoneNum, setPhoneNum] = useState("");
    const [signInPhoneNumberInvalid, setSignInPhoneNumberInvalid] = useState(false);
    const [isContinueBtnLoading, setIsContinueBtnLoading] = useState(false);
    const [cookies] = useCookies(["referralCode"]);
    const { showToast } = useToast();

    const [, setGoogleLoginErrState] = useRecoilState(googleLoginErrStateSelector);

    const checkPhoneRegex = (): boolean => {
        // E.164 format validation: +[country code][national number]
        // Examples: +49123456789, +13025551234, +447911123456
        const e164Regex = new RegExp(/^\+[1-9]\d{1,14}$/);
        return e164Regex.test(phoneNum);
    };

    const checkPhone = async (): Promise<void> => {
        setGoogleLoginErrState("");
        if (checkPhoneRegex()) {
            setIsContinueBtnLoading(true);
            setSignInPhoneNumberInvalid(false);
            setPhoneNumFromPhonePopup(phoneNum);

            await callApi("post", `${process.env.REACT_APP_ENV}-anystay-auth`, "user/exists", {
                body: {
                    emailLogin: false,
                    username: phoneNum,
                },
            })
                .then(async (res) => {
                    setIsContinueBtnLoading(false);
                    if (res.message === "User already exists") {
                        setUsername(res.username);
                        setPageName("PasswordPopup");
                    } else if (res.message === "User already exists but not attributes are verified") {
                        setPageName("SignInVerificationPopup");
                    } else if (res.message === "There are no users with that username") {
                        setPageName("SignUpFormPopup");
                    } else if (res.message === "You must sign in with Google") {
                        setSignInPhoneNumberInvalid(true);
                        showToast("You must sign in with Google", "error");
                    } else {
                        console.log("signin not working");
                    }
                })
                .catch((err) => {
                    setIsContinueBtnLoading(false);
                    console.log(err);
                });
        } else {
            setSignInPhoneNumberInvalid(true);
            showToast("Invalid phone number, try again!", "error");
        }
    };
    const getCurrentURL = () => {
        return window.location.pathname + window.location.search;
    };
    return (
        <div>
            <LogoImage src="https://d292awxalydr86.cloudfront.net/Branding/Anystay.svg" alt="Anystay Logo" />
            <CloseButton variant="outline" onClick={onClose}>
                <img 
                    src="https://d292awxalydr86.cloudfront.net/Universal+icons/Cross.svg" 
                    alt="Close"
                    width={16}
                    height={16}
                />
            </CloseButton>
            <AuthSignInSubPage>
                <AuthContentTitleWrapper>
                    <AuthContentTitle>Sign up or log in to continue</AuthContentTitle>
                </AuthContentTitleWrapper>
                <PhoneInputWrapper>
                    <StyledPhoneInput
                        placeholder="0000 000 000"
                        value={phoneNum}
                        defaultCountry={"AU"}
                        style={{
                            height: "54px",
                            marginBottom: "16px",
                            borderRadius: "12px",
                            border: `1px solid ${signInPhoneNumberInvalid ? ColorTheme.Red1.color : ColorTheme.Grey1.color}`,
                            paddingLeft: "16px",
                            paddingRight: "16px",
                        }}
                        onChange={async (value: string | undefined): Promise<void> => {
                            setSignInPhoneNumberInvalid(false);
                            setPhoneNum(value as string);
                        }}
                        onKeyDown={async (e: KeyboardEvent<HTMLInputElement>) => (e.key === "Enter" ? await checkPhone() : null)}
                        onBlur={handleKeyboardDismiss}
                    />
                </PhoneInputWrapper>

                <ContinueBtn
                    // style={{ marginTop: "0px", borderRadius: "12px" }}
                    background={"black"}
                    isLoading={isContinueBtnLoading}
                    disabled={isContinueBtnLoading}
                    continueType="submit"
                    onClick={async () => await checkPhone().catch((err) => console.log(err))}
                    round={true}
                >
                    Continue
                </ContinueBtn>
                <AuthSignInInputSubtitle>
                    We'll text you to confirm your number, standard rates apply.
                </AuthSignInInputSubtitle>

                <AuthSignInSocialButton
                    onClick={() => {
                        const currentURL = getCurrentURL();
                        const customStateData = {
                            redirectURL: currentURL,
                            referralCode: cookies.referralCode,
                            platform: "Google",
                        };
                        Auth.federatedSignIn({
                            provider: CognitoHostedUIIdentityProvider.Google,
                            customState: JSON.stringify(customStateData),
                        });
                    }}
                >
                    <AuthSignInSocialIcon src={"https://d292awxalydr86.cloudfront.net/Social-Icons/Google.svg"} alt={"Google icon"} />
                    <AuthSignInSocialTitle>Continue with google</AuthSignInSocialTitle>
                </AuthSignInSocialButton>
                {/* GOOGLE SIGN UP NOT WORKING */}
                {/* EMAIL SIGN UP STILL HAS BUGS */}
                {/* <AuthSignInSocialButton onClick={() => Auth.federatedSignIn({ provider: CognitoHostedUIIdentityProvider.Google })}>
                    <AuthSIgnInSocialIconWrapper>
                        <AuthSIgnInSocialIcon src={"https://d292awxalydr86.cloudfront.net/Universal+icons/Social+Media+Google.png"} alt={"Google icon"} />
                    </AuthSIgnInSocialIconWrapper>
                    <p>Continue with google</p>
                </AuthSignInSocialButton>
                <AuthSignInSocialButton style={{ marginTop: "24px" }} onClick={async () => {
                    await this.setState({ emailSignUp: true })
                    // this.toggleSignIn()
                    this.changePage(9)
                    // setTimeout(() => this.toggleSignUp(), 100)
                }}>
                    <AuthSIgnInSocialIconWrapper>
                        <img style={{ width: "100%", height: "100%" }} src={"https://d292awxalydr86.cloudfront.net/Social-Icons/Email.svg"} alt={"Email icon"} />
                    </AuthSIgnInSocialIconWrapper>
                    <p>Continue with email</p>
                </AuthSignInSocialButton> */}
                {/* <AuthSignInSocialButton onClick={() => Auth.federatedSignIn({ provider: CognitoHostedUIIdentityProvider.Google })}>Open Google</AuthSignInSocialButton> */}
            </AuthSignInSubPage>
        </div>
    );
};

export default PhonePopup;
